import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { CRMContactModal } from '../components/CRMContactModal';
import {
  Plus, MagnifyingGlass, Sparkle, Trash,
  Users, ChartLineUp, Star, CurrencyEur, Bell,
  DownloadSimple, UploadSimple, CalendarBlank,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/crm`;

const STAGE_ACCENT = {
  new:        '#6B7280',
  contacted:  '#3B82F6',
  qualified:  '#A855F7',
  proposal:   '#F59E0B',
  won:        '#10B981',
  lost:       '#EF4444',
};

const scoreColor = (score) => {
  if (score == null) return '#6B7280';
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#A855F7';
  if (score >= 40) return '#F59E0B';
  return '#EF4444';
};

const fmtEUR = (v) => {
  const n = Number(v) || 0;
  if (n >= 1000000) return `€${(n / 1000000).toFixed(1).replace('.0', '')}M`;
  if (n >= 1000) return `€${(n / 1000).toFixed(1).replace('.0', '')}k`;
  return `€${Math.round(n)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const followupStatus = (contact) => {
  const na = contact.next_action_at;
  if (!na || contact.stage === 'won' || contact.stage === 'lost') return null;
  const today = todayISO();
  if (na < today) return 'overdue';
  if (na === today) return 'today';
  return 'upcoming';
};


// ─────────────────────── Stat card ───────────────────────


const StatCard = ({ icon: Icon, label, value, accent = '#2563EB', testId }) => (
  <div
    className="p-5 bg-[#0F0F0F] border border-white/10 rounded-2xl"
    data-testid={testId}
    style={{ borderColor: `${accent}33` }}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">{label}</span>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22` }}>
        <Icon size={14} weight="bold" style={{ color: accent }} />
      </div>
    </div>
    <div className="text-3xl font-[Outfit] font-bold" style={{ color: accent }}>{value}</div>
  </div>
);


// ─────────────────────── Contact card ───────────────────────


const ContactCard = ({ contact, onEdit, onScore, onDelete, isScoring, t }) => {
  const initials = (contact.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const fu = followupStatus(contact);
  const fuColor = fu === 'overdue' ? '#EF4444' : fu === 'today' ? '#F59E0B' : '#6B7280';
  const fuLabel = fu === 'overdue'
    ? t('crm.badge_overdue')
    : fu === 'today'
      ? t('crm.badge_today')
      : contact.next_action_at
        ? new Date(contact.next_action_at + 'T00:00:00').toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
        : '';
  return (
    <div
      data-testid={`contact-card-${contact.contact_id}`}
      className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 hover:border-white/25 transition-all cursor-pointer group"
      onClick={() => onEdit(contact)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2563EB] to-[#3B82F6] flex items-center justify-center text-xs font-bold text-white shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{contact.name}</div>
            {contact.company && <div className="text-[11px] text-white/50 truncate">{contact.company}</div>}
          </div>
        </div>
        {contact.score != null && (
          <div
            className="text-xs font-bold px-2 py-0.5 rounded-md shrink-0"
            style={{ background: `${scoreColor(contact.score)}22`, color: scoreColor(contact.score) }}
            data-testid={`contact-score-${contact.contact_id}`}
          >
            {contact.score}
          </div>
        )}
      </div>

      {(contact.value > 0 || fu) && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {contact.value > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#34D399] border border-[#10B981]/25"
              data-testid={`contact-value-${contact.contact_id}`}
            >
              {fmtEUR(contact.value)}
            </span>
          )}
          {fu && (
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
              style={{ background: `${fuColor}22`, color: fuColor, border: `1px solid ${fuColor}44` }}
              data-testid={`contact-followup-${contact.contact_id}`}
              title={contact.next_action_note || ''}
            >
              <CalendarBlank size={10} weight="bold" />
              {fuLabel}
            </span>
          )}
        </div>
      )}

      {contact.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {contact.tags.slice(0, 4).map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onScore(contact.contact_id); }}
          disabled={isScoring}
          data-testid={`btn-score-${contact.contact_id}`}
          className="text-[10px] flex items-center gap-1 px-2 py-1 rounded bg-[#A855F7]/15 text-[#C084FC] hover:bg-[#A855F7]/25 transition-all disabled:opacity-50"
        >
          <Sparkle size={10} weight="bold" />
          {isScoring ? t('crm.scoring') : t('crm.score_with_ai')}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(contact); }}
          data-testid={`btn-delete-${contact.contact_id}`}
          className="text-[10px] p-1 rounded text-white/40 hover:text-[#EF4444] transition-colors"
          aria-label="delete"
        >
          <Trash size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
};


// ─────────────────────── Main page ───────────────────────


function CRMPage() {
  const { t } = useTranslation();
  const [pipeline, setPipeline] = useState({ stages: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [followupFilter, setFollowupFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [scoringId, setScoringId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [pipeRes, statsRes] = await Promise.all([
        axios.get(`${API}/pipeline`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
      ]);
      setPipeline(pipeRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Refetch on brand switch (Agency Mode)
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('brand:changed', handler);
    return () => window.removeEventListener('brand:changed', handler);
  }, [loadAll]);

  const filteredStages = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    const today = todayISO();
    const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    return pipeline.stages.map((s) => ({
      ...s,
      contacts: s.contacts.filter((c) => {
        const matchesQ = !q ||
          (c.name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.company || '').toLowerCase().includes(q);
        const matchesTag = !tagFilter || (c.tags || []).includes(tagFilter);
        let matchesFu = true;
        if (followupFilter) {
          const na = c.next_action_at;
          if (!na) {
            matchesFu = false;
          } else if (followupFilter === 'overdue') {
            matchesFu = na < today;
          } else if (followupFilter === 'today') {
            matchesFu = na === today;
          } else if (followupFilter === 'week') {
            matchesFu = na >= today && na <= weekEnd;
          }
        }
        return matchesQ && matchesTag && matchesFu;
      }),
    }));
  }, [pipeline, searchQ, tagFilter, followupFilter]);

  const openNew = () => { setEditingContact(null); setModalOpen(true); };
  const openEdit = (contact) => { setEditingContact(contact); setModalOpen(true); };

  const onSave = async (payload, contactId) => {
    if (contactId) {
      await axios.patch(`${API}/contacts/${contactId}`, payload, { withCredentials: true });
      toast.success(t('crm.updated'));
    } else {
      await axios.post(`${API}/contacts`, payload, { withCredentials: true });
      toast.success(t('crm.created'));
    }
    loadAll();
  };

  const onScore = async (contactId) => {
    setScoringId(contactId);
    try {
      const res = await axios.post(`${API}/contacts/${contactId}/score`, {}, { withCredentials: true });
      toast.success(`${t('crm.scored')} · ${res.data.score}/100`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('crm.score_failed'));
    } finally {
      setScoringId(null);
    }
  };

  const onDelete = async (contact) => {
    if (!window.confirm(t('crm.delete_confirm'))) return;
    try {
      await axios.delete(`${API}/contacts/${contact.contact_id}`, { withCredentials: true });
      toast.success(t('crm.deleted'));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('common.error_generic'));
    }
  };

  const onDragStart = (e, contactId) => {
    e.dataTransfer.setData('contact_id', contactId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = async (e, newStage) => {
    e.preventDefault();
    const cid = e.dataTransfer.getData('contact_id');
    if (!cid) return;
    const current = pipeline.stages.find((s) => s.contacts.some((c) => c.contact_id === cid));
    if (!current || current.key === newStage) return;
    try {
      await axios.patch(`${API}/contacts/${cid}`, { stage: newStage }, { withCredentials: true });
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error_generic'));
    }
  };

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/export`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'funnelhack_crm_contacts.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('crm.export_failed'));
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/import`, fd, { withCredentials: true });
      toast.success(t('crm.import_done', { imported: res.data.imported, skipped: res.data.skipped }));
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('crm.import_failed'));
    } finally {
      setImporting(false);
    }
  };

  const allTags = stats?.top_tags || [];

  return (
    <DashboardLayout user={user} activePage="/crm">
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="crm-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-[Outfit] font-bold text-white mb-2">{t('crm.title')}</h1>
            <p className="text-white/60 text-sm">{t('crm.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="btn-import-csv"
              className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
            >
              <UploadSimple size={15} weight="bold" />
              {importing ? t('crm.importing') : t('crm.import_csv')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onImportFile}
              className="hidden"
              data-testid="import-file-input"
            />
            <button
              onClick={onExport}
              data-testid="btn-export-csv"
              className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all"
            >
              <DownloadSimple size={15} weight="bold" />
              {t('crm.export_csv')}
            </button>
            <button
              onClick={openNew}
              data-testid="btn-new-contact"
              className="px-5 py-2.5 bg-[#2563EB] hover:bg-[#3B82F6] text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus size={16} weight="bold" />
              {t('crm.new_contact')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <StatCard icon={Users}        label={t('crm.stat_total')}     value={stats.total} accent="#2563EB" testId="stat-total" />
            <StatCard icon={CurrencyEur}  label={t('crm.stat_pipeline')}  value={fmtEUR(stats.open_value)} accent="#10B981" testId="stat-pipeline-value" />
            <StatCard icon={ChartLineUp}  label={t('crm.stat_forecast')}  value={fmtEUR(stats.weighted_forecast)} accent="#F59E0B" testId="stat-forecast" />
            <StatCard icon={Bell}         label={t('crm.stat_overdue')}   value={stats.overdue_count} accent={stats.overdue_count > 0 ? '#EF4444' : '#6B7280'} testId="stat-overdue" />
            <StatCard icon={Star}         label={t('crm.stat_avg_score')} value={stats.avg_score ?? '—'} accent="#A855F7" testId="stat-avg-score" />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="crm-search"
              type="text"
              placeholder={t('crm.search_placeholder')}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#2563EB] focus:outline-none"
            />
          </div>
          <select
            data-testid="crm-followup-filter"
            value={followupFilter}
            onChange={(e) => setFollowupFilter(e.target.value)}
            className="px-3 py-2 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#2563EB] focus:outline-none"
            style={followupFilter === 'overdue' ? { borderColor: '#EF4444' } : undefined}
          >
            <option value="">{t('crm.followup_all')}</option>
            <option value="overdue">{t('crm.followup_overdue')}</option>
            <option value="today">{t('crm.followup_today')}</option>
            <option value="week">{t('crm.followup_week')}</option>
          </select>
          <select
            data-testid="crm-tag-filter"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="px-3 py-2 bg-[#0F0F0F] border border-white/15 rounded-lg text-white text-sm focus:border-[#2563EB] focus:outline-none"
          >
            <option value="">{t('crm.filter_all_tags')}</option>
            {allTags.map((tag) => (
              <option key={tag.tag} value={tag.tag}>{tag.tag} ({tag.count})</option>
            ))}
          </select>
        </div>

        {/* Kanban */}
        {loading ? (
          <div className="text-center py-20 text-white/40 text-sm">{t('common.loading')}</div>
        ) : pipeline.total === 0 ? (
          <div className="text-center py-20 text-white/40 text-sm" data-testid="crm-empty">
            {t('crm.no_contacts')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kanban-board">
            {filteredStages.map((stage) => {
              const accent = STAGE_ACCENT[stage.key];
              return (
                <div
                  key={stage.key}
                  data-testid={`kanban-column-${stage.key}`}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3 min-h-[400px]"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDrop(e, stage.key)}
                  style={{ borderTopColor: accent, borderTopWidth: 2 }}
                >
                  <div className="flex items-center justify-between mb-1 px-1">
                    <span className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: accent }}>
                      {t(`crm.stage_${stage.key}`)}
                    </span>
                    <span className="text-xs text-white/40 font-semibold" data-testid={`stage-count-${stage.key}`}>
                      {stage.contacts.length}
                    </span>
                  </div>
                  <div className="px-1 mb-3 h-4">
                    {stage.value_total > 0 && (
                      <span className="text-[10px] font-bold text-white/50" data-testid={`stage-value-${stage.key}`}>
                        {fmtEUR(stage.value_total)}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {stage.contacts.map((c) => (
                      <div
                        key={c.contact_id}
                        draggable
                        onDragStart={(e) => onDragStart(e, c.contact_id)}
                      >
                        <ContactCard
                          contact={c}
                          onEdit={openEdit}
                          onScore={onScore}
                          onDelete={onDelete}
                          isScoring={scoringId === c.contact_id}
                          t={t}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CRMContactModal
        contact={editingContact}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={onSave}
        t={t}
      />
    </DashboardLayout>
  );
}

export default CRMPage;
